/**
 * 全局配置 — 所有魔法数字集中于此,禁止在别处硬编码。
 * (对应开发文档 §0.3:轮询间隔/里程碑门槛/效果参数 全部配置化)
 */

// ── B站视频(每期发布后追加 BVID,轮询遍历;老视频弹幕永远有效)──
export const BILI = {
  /** 当前主展示视频(左侧 iframe)。留空 = 显示世界观海报兜底;发布第一期后填真实 BVID。
   *  (旧值是假占位 BV 号,iframe 会渲染 B 站错误页直接上镜——比空更糟) */
  activeBvid: 'BV1QbKz67Ea3',
  /** 所有纳入世界的视频(Phase 1 轮询用) */
  bvids: ['BV1QbKz67Ea3'] as string[],
  /** 数据轮询周期(锁定决策 = 30 秒) */
  pollIntervalSec: 30,
  playerBase: '//player.bilibili.com/player.html',
  /** 播放器初始定格秒数:停在明亮的一帧(而非黑暗开场首帧)。0=不定格。换视频时按需调整。 */
  posterSeek: 138, // 2:18,正好是封面那帧
};

export function playerSrc(bvid: string): string {
  const t = BILI.posterSeek > 0 ? `&t=${BILI.posterSeek}` : '';
  return `${BILI.playerBase}?bvid=${bvid}&autoplay=0&danmaku=1${t}`;
}

// ── 服务端世界同步 WebSocket(Phase 3)──
export const NET = {
  /** dev 连本地服务端;生产同源 wss;VITE_WS_URL 可覆盖。 */
  wsUrl:
    ((import.meta as any).env?.VITE_WS_URL as string | undefined) ||
    (typeof location !== 'undefined' && location.protocol === 'https:'
      ? `wss://${location.host}/ws`
      : 'ws://localhost:8000/ws'),
};

// ── 调色板(GDD 3.2)──
export const PALETTE = {
  skyNightTop: 0x0b1026,
  skyNightBottom: 0x1a2151,
  skyDayTop: 0x2a4a8a,
  skyDayBottom: 0x9ec5ea,
  skyDawnTop: 0x3a2b57,
  skyDawnBottom: 0xf4a26b,
  skyDuskTop: 0x241a3a,
  skyDuskBottom: 0xd06b7a,
  candle: 0xffb347, // 烛民暖橙
  candleGlow: 0xffd98a,
  island: 0x0a0f24,
  islandEdge: 0x2a2f55,
  cloud: 0x3a3f66,
  stone: 0x8a93b8,
  gold: 0xffd76b,
  faith: 0xff9d4d,
  aurora: [0x38f9d7, 0x4d8cff, 0xa66bff],
  rain: 0x8fb8ff,
  snow: 0xeaf2ff,
  ember: 0xff7a33,
};

// ── 纪元 / 香火里程碑主线(GDD 5.1)。门槛可运营调整。──
export interface Epoch {
  faith: number; // 累计香火门槛
  key: string;
  name: string; // 纪元名
  event: string; // 世界大事件描述
  effect?: string; // 触发的大神迹效果(可选)
}

export const EPOCHS: Epoch[] = [
  { faith: 0, key: 'chaos', name: '混沌纪', event: '世界一片灰暗,云海中只有一粒火种明灭' },
  { faith: 100, key: 'dawn', name: '黎明纪', event: '第一次日出:全屏金光,浮岛从云海中显形', effect: 'epoch_leap' },
  { faith: 500, key: 'life', name: '生命纪', event: '第一批12个烛民从火种中诞生', effect: 'life_tide' },
  { faith: 1000, key: 'bronze', name: '青铜纪', event: '村落+农田从地面生长,烛民开始劳作', effect: 'epoch_leap' },
  { faith: 2500, key: 'poem', name: '诗歌纪', event: '石碑广场落成,历史S级圣诗全部立起', effect: 'epoch_leap' },
  { faith: 5000, key: 'silver', name: '白银纪', event: '城市、灯火、飞鸟;夜晚全岛点灯', effect: 'epoch_leap' },
  { faith: 10000, key: 'gold', name: '黄金纪', event: '万币神像从岛心升起,碑座刻前100名认领UID', effect: 'statue' },
  { faith: 25000, key: 'starsea', name: '星海纪', event: '世界长出光之翼,浮岛缓缓升向星空', effect: 'epoch_leap' },
  { faith: 50000, key: 'ascend', name: '飞升纪', event: '神殿开启(决赛日大事件)', effect: 'epoch_leap' },
];

export function epochIndexForFaith(faith: number): number {
  let idx = 0;
  for (let i = 0; i < EPOCHS.length; i++) {
    if (faith >= EPOCHS[i].faith) idx = i;
  }
  return idx;
}

export function nextEpoch(faith: number): Epoch | null {
  for (const e of EPOCHS) if (e.faith > faith) return e;
  return null;
}

// ── 短期节奏机制(GDD 5.2)──
export const RHYTHM = {
  coinBurstThreshold: 50, // 单心跳投币增量 ≥ 此值 → 流星雨 + "香火鼎盛!"
  danmakuSynergy: 5, // 30s 窗口同类弹幕 ≥ 此值 → 升级为中神迹
  eventsPerHeartbeatCap: 20, // 单心跳事件上限,超出进入下一心跳队列
  perMidHashPerHeartbeat: 3, // 同一 mid_hash 单心跳只取前 N 条,防刷屏
};

// ── 效果白名单(与 GDD 3.3 表逐条对应;任何输入只能映射到此枚举)──
export const EFFECT_WHITELIST = [
  // 轻神迹
  'rain', 'snow', 'wind', 'fireworks', 'rainbow', 'meteor', 'dance', 'sky_color',
  // 中神迹
  'aurora', 'thunderstorm', 'day_night_flip', 'life_tide', 'whale',
  // 大神迹
  'epoch_leap', 'statue', 'world_tree', 'meteor_shower',
  // 戏剧事件
  'sky_split',
  // 安全兜底(违规内容转化)
  'dark_cloud',
] as const;

// ── 调试模式 ──
// 神谕模拟器只允许开发者使用:普通用户能影响世界的途径只有 B 站弹幕/投币/评论。
// 访问 https://youboss.top/?debug=sky1 才会挂载模拟器面板与 ` 快捷键。
export const DEBUG =
  typeof location !== 'undefined' && new URLSearchParams(location.search).get('debug') === 'sky1';

// ── 世界初始态 ──
export const WORLD_INIT = {
  faith: 0,
  population: 0,
  onlineGods: 1,
};

// ── 渲染 ──
export const RENDER = {
  bg: 0x05070f,
  targetFps: 60,
  maxWeatherParticles: 900,
  maxEffectParticles: 1600,
};

// 移动端/小屏:降低粒子上限保帧率(P1-5)。以最短边判断,兼顾横竖屏。
if (typeof window !== 'undefined' && Math.min(window.innerWidth, window.innerHeight) < 640) {
  RENDER.maxWeatherParticles = 400;
  RENDER.maxEffectParticles = 720;
}

/**
 * P1-1:从服务端拉权威配置,覆盖本地 EPOCHS 门槛/名称与 RHYTHM(消除前后端双份漂移)。
 * 只改数值/名称,保留本地 event 文案(按 key 合并);拉不到或超时则用内置兜底。
 */
export async function syncConfigFromServer(httpBase: string): Promise<void> {
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 3500);
    const r = await fetch(httpBase + '/config', { signal: ctrl.signal });
    clearTimeout(timer);
    if (!r.ok) return;
    const cfg = await r.json();
    if (Array.isArray(cfg.epochs) && cfg.epochs.length) {
      const merged: Epoch[] = cfg.epochs.map((e: { faith: number; key: string; name: string; effect?: string }) => {
        const local = EPOCHS.find((x) => x.key === e.key);
        return { faith: e.faith, key: e.key, name: e.name, effect: e.effect ?? undefined, event: local?.event ?? '' };
      });
      EPOCHS.splice(0, EPOCHS.length, ...merged);
    }
    if (cfg.rhythm) {
      if (typeof cfg.rhythm.coinBurstThreshold === 'number') RHYTHM.coinBurstThreshold = cfg.rhythm.coinBurstThreshold;
      if (typeof cfg.rhythm.perMidHashPerHeartbeat === 'number') RHYTHM.perMidHashPerHeartbeat = cfg.rhythm.perMidHashPerHeartbeat;
    }
  } catch {
    /* 网络失败 → 用内置兜底,不阻塞启动 */
  }
}

/** 由 WS 地址推导 HTTP 基址(供 /config /claim /ledger 调用)。 */
export function httpBaseFromWs(): string {
  return NET.wsUrl.replace(/^ws/, 'http').replace(/\/ws$/, '');
}
